/**
 * Brand Rep signup + success experience.
 *
 * Two submission paths, chosen at runtime by whether the section's
 * `api_endpoint` setting is configured:
 *
 *  - ENDPOINT CONFIGURED (real backend deployed — see the
 *    ancarta-brand-rep-backend project): fetch()-based transaction against
 *    the real signup API. Only a `{ success: true }` response transforms
 *    the panel into the CONFIRMED success state with her real code. Any
 *    failure shows an inline error and lets her retry — never a fake
 *    success.
 *  - ENDPOINT BLANK (today's reality — nothing is deployed yet): the form
 *    submits natively, exactly as before. See the architecture note at the
 *    top of sections/brand-rep-signup.liquid for why that path exists and
 *    what it can/can't do. It shows the PENDING "Activating" state, never
 *    the confirmed one — the two states are visually and textually
 *    distinct (see showConfirmedSuccess vs hydratePendingSuccessState).
 */

const RESERVED_CODES = [
  'ADMIN', 'ANCARTA', 'OWNER', 'STAFF', 'TEAM', 'SUPPORT', 'WELCOME',
  'WELCOME20', 'SALE', 'FREE', 'DISCOUNT', 'TEST', 'NULL', 'UNDEFINED',
  'RUBY', 'BRANDREP', 'BRAND', 'REP', 'SHOPIFY', 'CODE',
];

const SESSION_KEY = 'ancartaBrandRepSubmission';
const SOURCE_KEY = 'ancartaBrandRepSource';
const ATTRIBUTION_KEY = 'ancartaBrandRepAttribution';

function publish(eventName, payload) {
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, payload || {});
    }
  } catch (error) {
    /* analytics must never break the funnel */
  }
}

function slugify(value, maxLength) {
  if (!value) return '';
  const cleaned = value
    .toString()
    .trim()
    .replace(/^@+/, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '');
  return maxLength ? cleaned.slice(0, maxLength) : cleaned;
}

function sanitizeCode(value) {
  return (value || '')
    .toString()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 12);
}

/**
 * Alternative suggestions offered on a collision — reserved-word (checked
 * client-side) or code-taken (only ever known via a real backend
 * response). Client-side generation only; never framed as an availability
 * check itself.
 */
function suggestAlternatives(baseCode) {
  const letters = (baseCode || '').replace(/[0-9]/g, '') || 'REP';
  const suffixes = ['G15', 'T15', 'A15', 'X15'];
  const seen = new Set();
  const suggestions = [];
  for (const suffix of suffixes) {
    const candidate = (letters + suffix).slice(0, 12);
    if (!RESERVED_CODES.includes(candidate) && !seen.has(candidate)) {
      seen.add(candidate);
      suggestions.push(candidate);
    }
    if (suggestions.length >= 3) break;
  }
  return suggestions;
}

function captureSource() {
  try {
    const existing = sessionStorage.getItem(SOURCE_KEY);
    if (existing) return existing;
  } catch (error) {
    /* sessionStorage may be unavailable (private mode, disabled) */
  }

  const params = new URLSearchParams(window.location.search);
  let source = params.get('utm_source');

  if (!source && document.referrer) {
    try {
      const referrerHost = new URL(document.referrer).hostname;
      if (/instagram\.com$/.test(referrerHost)) source = 'instagram';
      else if (/tiktok\.com$/.test(referrerHost)) source = 'tiktok';
      else source = referrerHost;
    } catch (error) {
      /* ignore unparsable referrer */
    }
  }

  source = source || 'direct';

  try {
    sessionStorage.setItem(SOURCE_KEY, source);
  } catch (error) {
    /* non-fatal */
  }

  return source;
}

export function readCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  } catch (error) {
    return '';
  }
}

/**
 * Phase 5B attribution capture (2026-09-06). Additive only — does not
 * change captureSource()'s existing behaviour/callers. Cached in
 * sessionStorage exactly like captureSource(), so the FIRST page load of a
 * session is what gets recorded even if she navigates to other pages
 * (losing the original URL's query string) before finishing the form. Never
 * fabricates a value: every field is either a real, directly-observed
 * string or `null` (meaning unavailable), matching the CAPI_ATTRIBUTION_DESIGN.md
 * / BRAND_REP_EVENT_TAXONOMY.md convention in the ancarta-meta-ads-agent
 * project this capture logic was designed against.
 *
 * Deliberately does NOT attempt to read the Instagram-app in-app-browser's
 * referrer any more reliably than captureSource() already does — that
 * hop is proven (see docs/ATTRIBUTION_AUDIT.md) to carry no ad-level
 * identifier no matter how it's read.
 */
export function captureAttribution() {
  try {
    const cached = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (cached) return JSON.parse(cached);
  } catch (error) {
    /* sessionStorage unavailable or cached value corrupt — recapture below */
  }

  const params = new URLSearchParams(window.location.search);
  const readParam = (name) => params.get(name) || null;

  const fbclid = readParam('fbclid');
  const existingFbc = readCookie('_fbc') || null;
  // Meta's documented fbc construction rule: fb.1.<ms-timestamp>.<fbclid> —
  // only ever built when a real fbclid is present; never fabricated from
  // nothing, and an existing _fbc cookie (set by the Meta Pixel itself, if
  // present on this page) always takes precedence over a freshly-built one.
  const fbc = existingFbc || (fbclid ? `fb.1.${Date.now()}.${fbclid}` : null);

  const attribution = {
    utm_source: readParam('utm_source'),
    utm_medium: readParam('utm_medium'),
    utm_campaign: readParam('utm_campaign'),
    utm_content: readParam('utm_content'),
    utm_term: readParam('utm_term'),
    // Populated only when a future direct-to-page Meta ad uses Dynamic URL
    // Parameters to append these — see CAMPAIGN_ARCHITECTURE_V2.md. Always
    // null for the current Instagram-profile-destination ads, which have no
    // destination URL at all to carry them.
    meta_campaign_id: readParam('meta_campaign_id'),
    meta_adset_id: readParam('meta_adset_id'),
    meta_ad_id: readParam('meta_ad_id'),
    meta_creative_id: readParam('meta_creative_id'),
    fbclid: fbclid,
    fbc: fbc,
    fbp: readCookie('_fbp') || null,
    landing_url: window.location.href,
    referrer: document.referrer || null,
    captured_at: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch (error) {
    /* non-fatal */
  }

  return attribution;
}

function generateIdempotencyKey() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (error) {
    /* fall through to the manual fallback below */
  }
  // Good enough entropy for an idempotency key (not a security token) on
  // browsers without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function buildBioText(firstName, code, shareUrl) {
  var lines = ['Ancarta Brand Rep 🤍', '15% OFF @ancarta.co — ' + code];
  // Visible bio text stays link-clean (no https://) — the copy button for
  // the link itself always copies the complete URL, set separately below.
  if (shareUrl) lines.push(shareUrl.replace(/^https:\/\//, ''));
  return lines.join('\n');
}

function initSignupForm() {
  const form = document.getElementById('brand-rep-signup-form');
  if (!form) return;

  const formPanel = form.querySelector('.br-signup__panel[data-state="form"]');
  const successPanel = form.querySelector('.br-signup__panel--success');
  const apiEndpoint = formPanel ? (formPanel.getAttribute('data-api-endpoint') || '').trim() : '';

  const fullNameInput = form.querySelector('[ref="fullNameInput"]');
  const firstNameInput = form.querySelector('[ref="firstNameInput"]');
  const lastNameInput = form.querySelector('[ref="lastNameInput"]');
  const instagramInput = form.querySelector('[ref="instagramInput"]');
  const emailInput = form.querySelector('[name="contact[email]"]');
  const tiktokInput = form.querySelector('[ref="tiktokInput"]');
  const codeInput = form.querySelector('[ref="codeInput"]');
  const codeStatus = form.querySelector('[ref="codeStatus"]');
  const tagsInput = form.querySelector('[ref="tagsInput"]');
  const termsCheckbox = form.querySelector('[ref="termsCheckbox"]');
  const submitButton = form.querySelector('[ref="submitButton"]');
  const submitLabel = submitButton ? submitButton.querySelector('.br-signup__submit-label') : null;
  const apiErrorMessage = form.querySelector('[ref="apiErrorMessage"]');
  const statusLive = form.querySelector('[ref="statusLive"]');
  const idempotencyKeyInput = form.querySelector('[ref="idempotencyKeyInput"]');

  if (idempotencyKeyInput) idempotencyKeyInput.value = generateIdempotencyKey();

  let formStarted = false;
  let isSubmitting = false;

  const markFormStarted = () => {
    if (formStarted) return;
    formStarted = true;
    publish('brand_rep_form_start', {});
  };

  function announce(message) {
    if (statusLive) statusLive.textContent = message;
  }

  function showCollisionSuggestions(baseValue, message) {
    if (!codeStatus) return;
    const alternatives = suggestAlternatives(baseValue.replace(/[0-9]/g, ''));
    codeStatus.textContent = '';
    codeStatus.setAttribute('data-valid', 'false');
    const label = document.createElement('span');
    label.textContent = message + ' ';
    codeStatus.appendChild(label);
    alternatives.forEach((alt, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'br-signup__code-suggestion';
      button.textContent = alt;
      button.addEventListener('click', () => {
        codeInput.value = alt;
        updateCodeStatus();
        codeInput.focus();
      });
      codeStatus.appendChild(button);
      if (index < alternatives.length - 1) codeStatus.appendChild(document.createTextNode(' '));
    });
  }

  function updateCodeStatus() {
    if (!codeInput || !codeStatus) return;
    const value = codeInput.value;

    if (!value) {
      codeStatus.textContent = '';
      codeStatus.removeAttribute('data-valid');
      return;
    }

    if (value.length < 3) {
      codeStatus.textContent = 'Use at least 3 characters.';
      codeStatus.setAttribute('data-valid', 'false');
      return;
    }

    if (RESERVED_CODES.includes(value)) {
      showCollisionSuggestions(value, 'That word is reserved — try:');
      return;
    }

    // Deliberately no positive "available"/"looks good" message here — this
    // is only a client-side format check (length, reserved words), not a
    // real Shopify availability check. Whether the code is actually free
    // is only known once the backend tries to create the real discount at
    // submission time (see submitToRealBackend's code_taken handling).
    codeStatus.textContent = '';
    codeStatus.removeAttribute('data-valid');
  }

  if (fullNameInput) {
    fullNameInput.addEventListener('input', markFormStarted);
  }

  if (codeInput) {
    codeInput.addEventListener('input', () => {
      markFormStarted();
      const cursorAtEnd = codeInput.selectionStart === codeInput.value.length;
      codeInput.value = sanitizeCode(codeInput.value);
      if (cursorAtEnd) codeInput.setSelectionRange(codeInput.value.length, codeInput.value.length);
      updateCodeStatus();
    });
  }

  [instagramInput, tiktokInput].forEach((input) => {
    if (!input) return;
    input.addEventListener('input', markFormStarted);
    input.addEventListener('blur', () => {
      if (!input.value) return;
      const handle = slugify(input.value, 30);
      input.value = handle ? '@' + handle : '';
    });
  });

  if (emailInput) emailInput.addEventListener('input', markFormStarted);

  captureSource();

  function setLoading(loading) {
    isSubmitting = loading;
    if (submitButton) submitButton.disabled = loading;
    if (submitLabel) submitLabel.textContent = loading ? 'CREATING YOUR BRAND REP PROFILE…' : 'CREATE MY BRAND REP PROFILE';
    if (loading) announce('Creating your Brand Rep profile, please wait.');
  }

  function showApiError(message) {
    if (!apiErrorMessage) return;
    apiErrorMessage.textContent = message;
    apiErrorMessage.hidden = false;
    apiErrorMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
    announce(message);
  }

  function clearApiError() {
    if (!apiErrorMessage) return;
    apiErrorMessage.hidden = true;
    apiErrorMessage.textContent = '';
  }

  function collectPayload() {
    const fullName = fullNameInput ? fullNameInput.value.trim() : '';
    return {
      fullName,
      email: emailInput ? emailInput.value.trim() : '',
      instagram: instagramInput ? instagramInput.value : '',
      tiktok: tiktokInput ? tiktokInput.value : '',
      code: codeInput ? sanitizeCode(codeInput.value) : '',
      // Single checkbox now covers Terms/Privacy + Brand Rep emails
      // together — see sections/brand-rep-signup.liquid and the backend's
      // validation.js for why this one value drives both.
      termsAccepted: termsCheckbox ? termsCheckbox.checked : false,
      source: captureSource(),
      attribution: captureAttribution(),
    };
  }

  async function submitToRealBackend(payload) {
    setLoading(true);
    clearApiError();

    let response;
    try {
      response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyInput ? idempotencyKeyInput.value : '',
        },
        body: JSON.stringify(payload),
      });
    } catch (networkError) {
      setLoading(false);
      showApiError('We couldn’t reach Ancarta right now. Check your connection and try again — nothing has been created yet.');
      return;
    }

    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      setLoading(false);
      showApiError('Something went wrong on our end. Please try again.');
      return;
    }

    setLoading(false);

    // A real response was received and understood — this specific attempt
    // is now fully resolved (success or a definitive error), so the next
    // submit must be treated as a NEW attempt, not a retry of this one.
    // Regenerating here (not in the network-error catch above, where no
    // response was ever received and reusing the same key is what keeps a
    // genuine connectivity-retry safely idempotent) fixes a real bug: the
    // backend caches every definitive response — including errors like
    // code_taken — under the Idempotency-Key header to guard against
    // double-submits. Without this, editing the code (or anything else)
    // after a rejection and resubmitting on the same page load would reuse
    // the same key and the backend would blindly replay the STALE cached
    // response from the earlier, different attempt — e.g. showing
    // "That code has already been taken" for a code that was never
    // actually checked.
    if (idempotencyKeyInput) idempotencyKeyInput.value = generateIdempotencyKey();

    if (response.ok && data && data.success) {
      showConfirmedSuccess(data);
      return;
    }

    // Every failure branch below leaves her entered data in the form and
    // lets her retry — never a fake success.
    if (data && data.error === 'code_taken') {
      showCollisionSuggestions(payload.code, data.message || 'That code has already been taken. Try another:');
      codeInput.focus();
      return;
    }
    if (data && data.error === 'existing_rep') {
      showApiError(
        (data.message || "It looks like you're already an Ancarta Brand Rep.") +
        (data.code ? ' Your code: ' + data.code : '')
      );
      return;
    }
    if (data && data.error === 'rate_limited') {
      showApiError('Too many attempts — please wait a minute and try again.');
      return;
    }
    if (data && data.error === 'validation' && Array.isArray(data.fieldErrors)) {
      showApiError(data.fieldErrors.map((e) => e.message).join(' '));
      return;
    }
    showApiError('Something went wrong creating your Brand Rep profile. Please try again.');
  }

  function showConfirmedSuccess(data) {
    if (!successPanel) return;

    const greetingEl = successPanel.querySelector('[ref="successGreeting"]');
    const codeLabelEl = successPanel.querySelector('[ref="codeLabel"]');
    const codeEl = successPanel.querySelector('[ref="successCode"]');
    const pendingNoteEl = successPanel.querySelector('[ref="codePendingNote"]');
    const bioTextEl = successPanel.querySelector('[ref="successBioText"]');
    const codeCopyComponent = successPanel.querySelector('[ref="codeCopyComponent"]');
    const bioCopyComponent = successPanel.querySelector('[ref="bioCopyComponent"]');
    const shareLinkCard = successPanel.querySelector('[ref="shareLinkCard"]');
    const shareLinkEl = successPanel.querySelector('[ref="successShareLink"]');
    const shareLinkCopyComponent = successPanel.querySelector('[ref="shareLinkCopyComponent"]');

    const bioText = data.bioText || buildBioText(data.firstName, data.code, data.shareUrl);

    if (greetingEl) greetingEl.textContent = data.firstName ? 'Welcome, ' + data.firstName + '.' : 'Welcome to Ancarta.';
    // Only this confirmed path ever claims the code is live — the label's
    // static default ("YOUR PERSONAL CODE") stays accurate for the pending
    // fallback below, which never reaches this function.
    if (codeLabelEl) codeLabelEl.textContent = 'YOUR CODE IS ACTIVE';
    if (codeEl) codeEl.textContent = data.code;
    if (pendingNoteEl) pendingNoteEl.hidden = true;
    if (bioTextEl) bioTextEl.textContent = bioText;
    if (codeCopyComponent && data.code) codeCopyComponent.setAttribute('text-to-copy', data.code);
    if (bioCopyComponent) bioCopyComponent.setAttribute('text-to-copy', bioText);

    // The share-link card starts `hidden` in the markup — only reveal it
    // once a real redirect genuinely exists (data.shareUrl is only ever set
    // by the backend after ensureShareLinkRedirect actually succeeded).
    // Never fabricate a link from just the code if the backend didn't
    // confirm one — see signup.js's rollback/no-orphan-redirect design.
    if (data.shareUrl) {
      if (shareLinkEl) shareLinkEl.textContent = data.shareUrl.replace(/^https:\/\//, '');
      if (shareLinkCopyComponent) shareLinkCopyComponent.setAttribute('text-to-copy', data.shareUrl);
      if (shareLinkCard) shareLinkCard.hidden = false;
    } else if (shareLinkCard) {
      shareLinkCard.hidden = true;
    }

    if (formPanel) formPanel.hidden = true;
    successPanel.hidden = false;
    successPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    announce("You're officially an Ancarta Brand Rep.");

    publish('brand_rep_signup_complete', { source: captureSource(), backend: 'confirmed' });
    // Genuinely "created": this only runs after the real backend confirmed
    // the Shopify discount exists.
    publish('brand_rep_code_created', { code: data.code });
  }

  function prepareNamesFromFullName() {
    const fullName = fullNameInput ? fullNameInput.value.trim() : '';
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    if (firstNameInput) firstNameInput.value = firstName;
    if (lastNameInput) lastNameInput.value = lastName;
    return firstName;
  }

  if (apiEndpoint) {
    // Real backend path. The button is type="button" (see the Liquid
    // template) whenever apiEndpoint is configured, specifically so this
    // never has to cancel a native form submission — live testing showed a
    // real trusted click on a type="submit" button could occasionally
    // reach Shopify's own native `{% form 'customer' %}` handling (and its
    // bot challenge) despite this handler's own preventDefault() call,
    // even though that call is provably synchronous and unconditional on
    // this path. Removing the native submission from the equation entirely
    // removes whatever timing this depended on, rather than trying to win
    // a race against it.
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        if (isSubmitting) return; // guards double-click/repeat-tap, same as before

        // reportValidity() gives the same native required-field UI a
        // type="submit" button would have produced for free.
        if (!form.reportValidity()) return;

        if (codeInput && RESERVED_CODES.includes(codeInput.value)) {
          updateCodeStatus();
          codeInput.focus();
          return;
        }

        prepareNamesFromFullName();
        submitToRealBackend(collectPayload());
      });
    }
    return;
  }

  // No backend deployed — native-form fallback (unchanged behaviour): the
  // button stays a real type="submit", so this listener's job is only to
  // prepare the hidden fields/session snapshot before Shopify's own native
  // `{% form 'customer' %}` processing takes over.
  form.addEventListener('submit', (event) => {
    if (isSubmitting) {
      event.preventDefault();
      return;
    }

    if (codeInput && RESERVED_CODES.includes(codeInput.value)) {
      event.preventDefault();
      updateCodeStatus();
      codeInput.focus();
      return;
    }

    const firstName = prepareNamesFromFullName();
    const igSlug = slugify(instagramInput ? instagramInput.value : '', 30);
    const ttSlug = slugify(tiktokInput ? tiktokInput.value : '', 30);
    const code = codeInput ? sanitizeCode(codeInput.value) : '';
    const source = slugify(captureSource(), 30);

    const tags = ['brand-rep-pending'];
    if (igSlug) tags.push('br-ig-' + igSlug);
    if (ttSlug) tags.push('br-tt-' + ttSlug);
    if (code) tags.push('br-code-' + code.toLowerCase());
    if (source) tags.push('br-source-' + source);
    if (tagsInput) tagsInput.value = tags.join(',');

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        firstName,
        code,
        instagram: instagramInput ? instagramInput.value : '',
        email: emailInput ? emailInput.value.trim() : '',
      }));
    } catch (error) {
      /* non-fatal: success screen falls back to generic copy */
    }

    setLoading(true);
  });
}

/**
 * PENDING success state — only reached via the native-form fallback path
 * (see the doc comment at the top of this file). Deliberately distinct
 * from showConfirmedSuccess: this never removes the "Activating" badge or
 * the pending note, and fires brand_rep_code_requested, not
 * brand_rep_code_created.
 */
function hydratePendingSuccessState() {
  const successPanel = document.querySelector('.br-signup__panel--success');
  if (!successPanel || successPanel.hasAttribute('hidden')) return;

  let snapshot = null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) snapshot = JSON.parse(raw);
    sessionStorage.removeItem(SESSION_KEY);
  } catch (error) {
    snapshot = null;
  }

  const greetingEl = successPanel.querySelector('[ref="successGreeting"]');
  const codeEl = successPanel.querySelector('[ref="successCode"]');
  const bioTextEl = successPanel.querySelector('[ref="successBioText"]');
  const codeCopyComponent = successPanel.querySelector('[ref="codeCopyComponent"]');
  const bioCopyComponent = successPanel.querySelector('[ref="bioCopyComponent"]');

  const firstName = snapshot && snapshot.firstName ? snapshot.firstName : '';
  const code = snapshot && snapshot.code ? snapshot.code : '';

  if (greetingEl) greetingEl.textContent = firstName ? 'Welcome, ' + firstName + '.' : 'Welcome to Ancarta.';
  if (codeEl) codeEl.textContent = code || 'Pending';

  // No literal fallback like "YOURCODE" here — an unconfirmed code truly
  // isn't known yet, so buildBioText with an empty code just trails off
  // after the em dash rather than showing something that reads as real.
  const bioText = buildBioText(firstName, code);
  if (bioTextEl) bioTextEl.textContent = bioText;
  if (codeCopyComponent && code) codeCopyComponent.setAttribute('text-to-copy', code);
  if (bioCopyComponent) bioCopyComponent.setAttribute('text-to-copy', bioText);

  publish('brand_rep_signup_complete', {
    source: (function () {
      try {
        return sessionStorage.getItem(SOURCE_KEY) || 'direct';
      } catch (error) {
        return 'direct';
      }
    }()),
    backend: 'pending_fallback',
  });

  if (code) {
    /*
      "requested", not "created": no real Shopify discount exists yet at
      this point (see the architecture note in brand-rep-signup.liquid) —
      naming this event brand_rep_code_created would misrepresent what
      actually happened.
    */
    publish('brand_rep_code_requested', { code_requested: code });
  }
}

/**
 * Mobile-only sticky "BECOME A BRAND REP" CTA (markup lives in
 * sections/brand-rep-hero.liquid, [ref="brStickyCta"], starts [hidden]).
 * Desktop is excluded entirely by CSS (`display: none !important` ≥750px)
 * — this only ever toggles the [hidden] attribute, never layout, so there
 * is no layout shift either way.
 *
 * Visible only once ALL of:
 *  - the visitor has scrolled past the hero IMAGE specifically (its box has
 *    scrolled fully above the viewport top) — not the whole hero section,
 *    which also includes the copy block above the image and a padding
 *    strip below it; watching the image/frame itself (rather than the
 *    section) means the CTA can appear as soon as the last model is off
 *    screen, without waiting for that extra padding to clear too
 *  - the signup section has never been reached yet (hides as soon as it
 *    enters view, and — once reached — stays hidden even if she later
 *    scrolls past it into the footer, since she's already seen the real CTA)
 *  - the success state is NOT being shown (checked via the success panel's
 *    own [hidden] attribute — the same flag showConfirmedSuccess/
 *    hydratePendingSuccessState already flip)
 *  - Shopify's own cookie-consent banner (#shopify-pc__banner) is not
 *    currently on screen, so the two fixed bottom bars never stack
 */
function initStickyCta() {
  const sticky = document.querySelector('[ref="brStickyCta"]');
  // The mobile hero photo's own frame (sections/brand-rep-hero.liquid),
  // not the whole <section id="brand-rep-hero">, which also contains the
  // copy block above it and a padding strip below it — see the doc
  // comment above. Falls back to the whole hero section if the frame
  // isn't found for some reason (e.g. a future markup change), so this
  // never silently breaks the feature.
  const heroImageFrame = document.querySelector('.br-hero__frame-inner--mobile') || document.getElementById('brand-rep-hero');
  const signupSection = document.getElementById('brand-rep-signup');
  if (!sticky || !heroImageFrame || !signupSection) return;

  const successPanel = signupSection.querySelector('.br-signup__panel--success');

  const state = {
    pastHero: false,
    signupVisible: true,
    // Once she's scrolled far enough to reach the signup section at least
    // once, she's already seen the real CTA — the sticky shortcut has done
    // its job and shouldn't reappear if she keeps scrolling into the
    // footer past it.
    reachedSignup: false,
    successVisible: successPanel ? !successPanel.hidden : false,
  };

  function isCookieBannerVisible() {
    const banner = document.getElementById('shopify-pc__banner');
    if (!banner) return false;
    const style = window.getComputedStyle(banner);
    return style.display !== 'none' && style.visibility !== 'hidden' && banner.offsetHeight > 0;
  }

  function render() {
    const shouldShow = state.pastHero && !state.signupVisible && !state.successVisible && !isCookieBannerVisible();
    // Guard against a redundant write: sticky itself lives under document.body,
    // and the body-wide MutationObserver below watches 'hidden' (among other
    // attributes) so it can catch the cookie banner's visibility changing in
    // place. Writing sticky.hidden unconditionally would re-trigger that same
    // observer on every render() call — including calls the observer itself
    // caused — creating an infinite self-triggering mutation loop. Only
    // writing when the value actually changes breaks that loop.
    if (sticky.hidden !== !shouldShow) sticky.hidden = !shouldShow;
  }

  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        state.pastHero = entry.boundingClientRect.bottom < 0;
        render();
      });
    }, { threshold: 0 }).observe(heroImageFrame);

    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) state.reachedSignup = true;
        state.signupVisible = state.reachedSignup ? true : entry.isIntersecting;
        render();
      });
    }, { threshold: 0.1 }).observe(signupSection);
  }

  if (successPanel) {
    new MutationObserver(() => {
      state.successVisible = !successPanel.hidden;
      render();
    }).observe(successPanel, { attributes: true, attributeFilter: ['hidden'] });
  }

  // Shopify's privacy-consent script may insert/remove #shopify-pc__banner
  // outright, OR toggle its visibility in place via a style/class change on
  // the same node (its own future implementation isn't guaranteed to pick
  // one or the other) — childList alone would miss the second case, so this
  // also watches style/class/hidden attribute changes anywhere under body.
  // attributeFilter keeps it cheap: it only fires for those three
  // attributes, not every attribute mutation on the page.
  new MutationObserver(render).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });

  render();
}

function initAnalyticsDelegation() {
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-br-analytics]');
    if (!trigger) return;
    trigger.getAttribute('data-br-analytics').split(',').forEach((eventName) => {
      publish(eventName.trim(), {});
    });
  });

  document.querySelectorAll('.br-success__copy-button').forEach((button) => {
    button.addEventListener('click', () => {
      const isSecondary = button.classList.contains('br-success__copy-button--secondary');
      publish(isSecondary ? 'brand_rep_bio_copied' : 'brand_rep_code_copied', {});
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', ready);
} else {
  ready();
}

function ready() {
  if (document.getElementById('brand-rep-hero') || document.getElementById('brand-rep-signup')) {
    publish('brand_rep_page_view', {});
  }
  initSignupForm();
  hydratePendingSuccessState();
  initAnalyticsDelegation();
  initStickyCta();
}
